import React, { useState, useEffect } from 'react';
import api from '../lib/api';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from './ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from './ui/select';
import { Loader2, Search, User } from 'lucide-react';
import { ScrollArea } from './ui/scroll-area';
import { toast } from 'sonner';

const CATEGORIES = [
  { value: 'general', label: 'General Enquiry' },
  { value: 'installation', label: 'Installation Issue' },
  { value: 'billing', label: 'Billing / Payment' },
  { value: 'monitoring', label: 'Monitoring / System' },
  { value: 'warranty', label: 'Warranty' },
  { value: 'maintenance', label: 'Maintenance' },
  { value: 'other', label: 'Other' },
];

const PRIORITIES = [
  { value: 'low', label: 'Low' },
  { value: 'medium', label: 'Medium' },
  { value: 'high', label: 'High' },
  { value: 'urgent', label: 'Urgent' },
];

export function RaiseTicketDialog({ open, onOpenChange, preselectedCustomer, onSuccess }) {
  const [subject, setSubject] = useState('');
  const [description, setDescription] = useState('');
  const [category, setCategory] = useState('general');
  const [priority, setPriority] = useState('medium');
  const [customerSearch, setCustomerSearch] = useState('');
  const [customerResults, setCustomerResults] = useState([]);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [searching, setSearching] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (open && preselectedCustomer) {
      setSelectedCustomer(preselectedCustomer);
      setCustomerSearch('');
    }
    if (!open) {
      setSubject('');
      setDescription('');
      setCategory('general');
      setPriority('medium');
      setCustomerSearch('');
      setCustomerResults([]);
      if (!preselectedCustomer) setSelectedCustomer(null);
    }
  }, [open, preselectedCustomer]);

  const searchCustomers = async (q) => {
    setCustomerSearch(q);
    if (q.length < 2) { setCustomerResults([]); return; }
    setSearching(true);
    try {
      const { data } = await api.get('/customers', { params: { search: q, limit: 8 } });
      setCustomerResults(data.customers || []);
    } catch {
      setCustomerResults([]);
    } finally {
      setSearching(false);
    }
  };

  const handleSubmit = async () => {
    if (!selectedCustomer) { toast.error('Please select a customer'); return; }
    if (!subject.trim()) { toast.error('Subject is required'); return; }
    if (!description.trim()) { toast.error('Description is required'); return; }

    setSubmitting(true);
    try {
      await api.post('/tickets/raise', {
        customer_id: selectedCustomer.id,
        subject: subject.trim(),
        description: description.trim(),
        category,
        priority,
      });
      toast.success('Support ticket raised successfully');
      onOpenChange(false);
      onSuccess?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to raise ticket');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle data-testid="raise-ticket-title">Raise Support Ticket</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          {/* Customer selector */}
          {!preselectedCustomer ? (
            <div>
              <Label className="text-xs font-medium text-muted-foreground">Customer</Label>
              {selectedCustomer ? (
                <div className="flex items-center justify-between p-2.5 border rounded-md mt-1 bg-muted/50">
                  <div className="flex items-center gap-2">
                    <User className="w-4 h-4 text-muted-foreground" />
                    <span className="text-sm font-medium">
                      {selectedCustomer.first_name} {selectedCustomer.last_name}
                    </span>
                    <span className="text-xs text-muted-foreground">{selectedCustomer.email}</span>
                  </div>
                  <Button variant="ghost" size="sm" className="h-7 text-xs" onClick={() => setSelectedCustomer(null)}>
                    Change
                  </Button>
                </div>
              ) : (
                <div className="space-y-1 mt-1">
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                    <Input
                      data-testid="ticket-customer-search"
                      placeholder="Search customer by name or email..."
                      className="pl-9"
                      value={customerSearch}
                      onChange={(e) => searchCustomers(e.target.value)}
                    />
                  </div>
                  {(customerResults.length > 0 || searching) && (
                    <ScrollArea className="max-h-40 border rounded-md">
                      {searching ? (
                        <div className="flex items-center justify-center py-3">
                          <Loader2 className="w-4 h-4 animate-spin" />
                        </div>
                      ) : (
                        customerResults.map((c) => (
                          <button
                            key={c.id}
                            data-testid={`ticket-customer-${c.id}`}
                            className="w-full text-left px-3 py-2 hover:bg-muted text-sm flex items-center gap-2"
                            onClick={() => { setSelectedCustomer(c); setCustomerResults([]); setCustomerSearch(''); }}
                          >
                            <User className="w-3.5 h-3.5 text-muted-foreground" />
                            <span className="font-medium">{c.first_name} {c.last_name}</span>
                            <span className="text-xs text-muted-foreground ml-auto">{c.email}</span>
                          </button>
                        ))
                      )}
                    </ScrollArea>
                  )}
                </div>
              )}
            </div>
          ) : (
            <div className="flex items-center gap-2 p-2.5 border rounded-md bg-muted/50">
              <User className="w-4 h-4 text-muted-foreground" />
              <span className="text-sm font-medium">
                {preselectedCustomer.first_name} {preselectedCustomer.last_name}
              </span>
              <span className="text-xs text-muted-foreground">{preselectedCustomer.email}</span>
            </div>
          )}

          {/* Subject */}
          <div>
            <Label className="text-xs font-medium text-muted-foreground">Subject</Label>
            <Input
              data-testid="ticket-subject"
              placeholder="Brief summary of the issue"
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              className="mt-1"
            />
          </div>

          {/* Category + Priority */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs font-medium text-muted-foreground">Category</Label>
              <Select value={category} onValueChange={setCategory}>
                <SelectTrigger className="mt-1" data-testid="ticket-category">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {CATEGORIES.map((c) => (
                    <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs font-medium text-muted-foreground">Priority</Label>
              <Select value={priority} onValueChange={setPriority}>
                <SelectTrigger className="mt-1" data-testid="ticket-priority">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {PRIORITIES.map((p) => (
                    <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Description */}
          <div>
            <Label className="text-xs font-medium text-muted-foreground">Description</Label>
            <Textarea
              data-testid="ticket-description"
              placeholder="Describe the issue in detail..."
              className="mt-1 min-h-[100px]"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button
            onClick={handleSubmit}
            disabled={submitting || !selectedCustomer || !subject.trim()}
            data-testid="submit-ticket-btn"
          >
            {submitting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
            Raise Ticket
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
